import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { Avatar, LanguageSwitcher, cn } from '@xdocs/ui';
import { roleSatisfies, useCurrentRole } from '../features/admin/index.js';
import { useSettingsProfile } from '../routes/settings/useSettingsProfile.js';
import { useLocaleController } from '../i18n/LocaleController.js';
import { useLogout } from '../ws/useLogout.js';
import { useWebT } from '../i18n/useWebT.js';
import styles from './AccountMenu.module.css';

/**
 * Avatar-triggered account popover (mockup ground truth:
 * `mockups-extracted/18-documents` topbar avatar menu — "My shares",
 * "Settings", "Style guide", role-gated "Admin console" + STAFF badge, an
 * inline language switcher, "Log out"). Consolidates controls the previous
 * implementation scattered as flat topbar buttons/sidebar links.
 *
 * `@xdocs/ui` has no anchored-popover primitive yet (same gap
 * `NotificationsBell` flags in its own module doc) and the native Popover
 * API's top-layer elements need CSS anchor positioning — not yet
 * broadly supported — to anchor under an arbitrary trigger without manual
 * `getBoundingClientRect` bookkeeping. A `position:absolute` panel inside a
 * `position:relative` wrapper, closed on outside-pointerdown/Escape with
 * focus returned to the trigger, gets the same user-observable behavior
 * ux.md's popover section asks for (dismiss outside-click + Esc, focus
 * back on close) without a new dependency.
 */
export function AccountMenu(): ReactElement {
  const { t } = useWebT();
  const { setLocale } = useLocaleController();
  const logout = useLogout();
  const { role } = useCurrentRole();
  const isStaff = roleSatisfies(role, 'moderator');
  const isAdmin = roleSatisfies(role, 'admin');
  const { profile } = useSettingsProfile();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const close = (): void => setOpen(false);

  async function handleLogout(): Promise<void> {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  }

  const accountName = profile?.displayName ?? profile?.email ?? '';

  return (
    <div className={cn(styles['wrapper'])} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={cn(styles['trigger'])}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('web:shell.menu.trigger')}
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={accountName} size={34} loading={!profile} />
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div id={menuId} className={cn(styles['panel'])} aria-label={t('web:shell.menu.trigger')}>
          {profile && (
            <div className={cn(styles['header'])}>
              <div className={cn(styles['name'])}>{accountName}</div>
              <div className={cn(styles['email'])}>{profile.email}</div>
            </div>
          )}
          <NavLink to="/shares" className={cn(styles['item'])} onClick={close}>
            {t('web:shell.menu.myShares')}
          </NavLink>
          <NavLink to="/settings" className={cn(styles['item'])} onClick={close}>
            {t('web:shell.nav.settings')}
          </NavLink>
          <NavLink to="/styleguide" className={cn(styles['item'])} onClick={close}>
            {t('web:shell.menu.styleguide')}
          </NavLink>
          {isStaff && (
            <NavLink
              to={isAdmin ? '/admin/overview' : '/admin/assets'}
              className={cn(styles['item'], styles['itemBetween'])}
              onClick={close}
            >
              {t('web:shell.menu.adminConsole')}
              <span className={cn(styles['staffBadge'])}>{t('web:shell.menu.staffBadge')}</span>
            </NavLink>
          )}
          <div className={cn(styles['languageRow'])}>
            <span>{t('web:shell.menu.language')}</span>
            <LanguageSwitcher onLocaleChange={setLocale} />
          </div>
          <button
            type="button"
            className={cn(styles['item'], styles['logoutItem'])}
            onClick={() => void handleLogout()}
            disabled={loggingOut}
          >
            {loggingOut ? t('web:shell.logout.confirming') : t('web:shell.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
